import AuthenticatedImage from 'src/components/Items/AuthenticatedImage';
import DataTable from 'src/components/Layouts/DataTable';
import React, { memo } from 'react';
import SVG from 'src/components/Images/SvgRenderer';
import Tooltip from 'src/components/Layouts/Tooltip';
import { createUseStyles } from 'react-jss';
import { getAppApiServer, getAppEnvironmentApiData } from 'src/utils/useApp';
import { getUserRole } from 'src/utils/useUser';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'src/hooks/redux-hooks';

const useStyles = createUseStyles((theme: any) => ({
  switchTableWrapper: {
    width: '80%',
  },
  userImage: {
    width: '24px',
    height: '24px',
    minWidth: '24px',
    minHeight: '24px',
    maxWidth: '24px',
    maxHeight: '24px',
    backgroundColor: theme.colors.white,
    borderRadius: '100%',
  },
  fullname: {
    display: 'block',
    maxWidth: '150px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: '500',
  },
  cellWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
  },
  environment: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: '600',
    backgroundColor: theme.colors.chip,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: theme.colors.grey[350],
    borderRadius: '20px',
    width: 'fit-content',
    padding: '0 4px',
    height: '20px',
    margin: 'auto 0',
  },
  smallButton: {
    cursor: 'pointer',
    width: '40px',
    height: '40px',
    '& > svg': {
      color: theme.colors.primaryBlue[250],
      width: '24px !important',
      height: '24px !important',
      transition: 'color 0.25s',
    },
    '&:hover':{
      '& > svg': {
        color: theme.colors.primaryBlue[500],
      },
    },
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    height: '100%',
    overflow: 'auto',
    maxWidth: '100%',
  },
}));

type ItemsType = {
  users: any;
  useApiServers: any;
  canEdit: any;
  handleContinueAsUser: any;
  handleDeleteUser: any;
};

const SwitchUserTable: React.FunctionComponent<ItemsType> = ({ users, useApiServers, canEdit, handleContinueAsUser, handleDeleteUser }) => {

  const classes = useStyles();
  const { t } = useTranslation();
  const userData = useAppSelector((state: any) => state.user);
  
  const FullNameCell = memo(
    ({ row }: { row: any; }) => (
      <>
        <AuthenticatedImage
          className={classes.userImage}
          thumbLink={row.photo}
          fullsizeLink={row.photo}
          preferQuality="thumb"
          ignoreInView={true}
          ignoreIsLoaded={true}
        />
        <span className={classes.fullname}>{row.firstname} {row.surname}</span>
      </>
    ),
    (prevProps, nextProps) => {
      return prevProps.row.firstname === nextProps.row.firstname && prevProps.row.surname === nextProps.row.surname && prevProps.row.photo.thumbLink === nextProps.row.photo.thumbLink;
    }
  );


  const columns: any = [
    { field: 'fullname', headerName: t('user'), renderCell: (params: any) => (
      <FullNameCell 
        row={params.row} 
      />
    ), display: 'flex', valueGetter: (_: any, row: any) => `${row.firstname} ${row.surname}`, sortComparator: (v1: string, v2: string) => v1.localeCompare(v2)},
    { field: 'firstname', headerName: t('firstname') },
    { field: 'surname', headerName: t('surname'), },
    { field: 'email', headerName: t('email'), },
    { field: 'roleType', headerName: t('role'), valueGetter: (_: any, row: any) => t(`role_${getUserRole(row.roleType)}`), },
    useApiServers ? { field: 'apiServer', headerName: t('api_server'), renderCell: (params: any) => (
      <div className={classes.cellWrapper}>
        <Tooltip title={getAppEnvironmentApiData(params.row.apiServer).name} position='bottom' arrow>
          <span className={classes.environment}>{getAppEnvironmentApiData(params.row.apiServer).shortName}</span>
        </Tooltip>
      </div>
    )} : null,
    { field: 'action', type: 'actions', headerName: t('children_action'), sortable: false, filterable: false, display: 'flex', hideable: false, getActions: (params: any) => [
      canEdit ? (
        <GridActionsCellItem
          icon={<Tooltip title={t('switch_user_remove_account')} position='bottom' maxWidth={400} arrow><SVG src="trash-outlined"/></Tooltip>}
          label={t('switch_user_remove_account')}
          onClick={() => handleDeleteUser(params.row.user)}
        />
      ) : (
        <GridActionsCellItem
          icon={<Tooltip title={params.row.accessToken ? (useApiServers ? (params.row.userID === userData.userObject.userID && params.row.apiServer === getAppApiServer()) : params.row.userID === userData.userObject.userID) ? t('switch_user_login_continue_current') : t("switch_user_login_continue") : t("switch_user_login_password")} position='bottom' maxWidth={400} arrow><SVG src={params.row.accessToken ? "login" : "input-field-outlined"}/></Tooltip>}
          label={params.row.accessToken ? (useApiServers ? (params.row.userID === userData.userObject.userID && params.row.apiServer === getAppApiServer()) : params.row.userID === userData.userObject.userID) ? t('switch_user_login_continue_current') : t("switch_user_login_continue") : t("switch_user_login_password")}
          onClick={(e: any) => handleContinueAsUser(e, params.row.user)}
        />
      ),
    ].filter((item: any) => item !== null) },
  ];

  const data = users.map((user: any) => {

    return {
      id: useApiServers ? `${user.userID}_${user.apiServer}` : user.userID,
      userID: user.userID,
      firstname: user.firstname,
      surname: user.surname,
      email: user.email,
      roleType: user.roleType,
      accessToken: user.accessToken,
      photo: user.photo,
      apiServer: user.apiServer,
      user: user,
    };

  });

  return (
    <DataTable classNameWrapper={classes.switchTableWrapper} columns={columns} data={data} page={0} visibleRows={[8, 16]} useAutoHeight={true} rowHeight={32} isColumnFilterable={true} isColumnMenuEnabled={true} isColumnSortable={true} isColumnSelectable={true} isFooterEnabled={true} isFooterPaginationEnabled={true} noResultsText={t('no_users_found')}/>
  );
}

export default SwitchUserTable;